// Static AAD v2.0 authority metadata fed to MSAL so it can skip its two
// pre-redirect discovery network calls (cloud instance discovery + the OpenID
// configuration fetch) and navigate to Entra immediately on "sign in".
//
// These endpoint shapes are standard and permanent for the Azure *public* cloud
// (login.microsoftonline.com) + AAD v2.0 — only the tenant GUID varies. Kept in
// their own module (no env assertion / no `window`) so the shapes can be pinned
// by a unit test; they were verified against the live
// `…/v2.0/.well-known/openid-configuration` response. MSAL only requires
// authorization_endpoint / token_endpoint / issuer / jwks_uri to be present
// (isOpenIdConfigResponse), and parses these with JSON.parse — a wrong value
// would surface on the next sign-in, never silently.

function authorityFor(tenantId: string): string {
  return `https://login.microsoftonline.com/${tenantId}`;
}

/** OpenID-configuration metadata (skips MSAL's openid-configuration fetch). */
export function buildAadV2AuthorityMetadata(tenantId: string): string {
  const authority = authorityFor(tenantId);
  return JSON.stringify({
    authorization_endpoint: `${authority}/oauth2/v2.0/authorize`,
    token_endpoint: `${authority}/oauth2/v2.0/token`,
    issuer: `${authority}/v2.0`,
    jwks_uri: `${authority}/discovery/v2.0/keys`,
    end_session_endpoint: `${authority}/oauth2/v2.0/logout`,
    response_modes_supported: ['query', 'fragment', 'form_post'],
    response_types_supported: ['code', 'id_token', 'code id_token', 'id_token token'],
  });
}

/** Cloud instance-discovery metadata for the public cloud (skips MSAL's instance discovery). */
export function buildAadPublicCloudDiscoveryMetadata(tenantId: string): string {
  const authority = authorityFor(tenantId);
  return JSON.stringify({
    tenant_discovery_endpoint: `${authority}/v2.0/.well-known/openid-configuration`,
    'api-version': '1.1',
    metadata: [
      {
        preferred_network: 'login.microsoftonline.com',
        preferred_cache: 'login.windows.net',
        aliases: [
          'login.microsoftonline.com',
          'login.windows.net',
          'login.microsoft.com',
          'sts.windows.net',
        ],
      },
    ],
  });
}
